import { useVisualViewport } from "./useVisualViewport";
import { BlockReasonSheet } from "./AccountBlocks";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ApiError, api } from "./api";
import { AppIcon, LoadingState, MenuGroup, MenuRow, ParticipantFrame, EmptyMark } from "./ParticipantShell";
import { BEFORE_WE_MEET_PROMPTS, CHANNEL_LABELS, CandidateProfile } from "./PairingExperience";
import { createIdempotencyKey, waitingPoolErrorMessage } from "../shared/waiting-pool-client";
import type {
  BeforeWeMeetAnswer, ConnectionEntry, ContactEntry, ConversationMessage, ConversationPage, ConversationSummary,
} from "./pairing-types";

/**
 * Connection Messages v1 (connection-messages.v1.md). Messages live inside a Connection: the Connections tab is the
 * conversation list, a conversation opens over it, and everything about the person sits one step further in.
 */

const POLL_MS = 4_000;
const LIST_POLL_MS = 30_000;
const MESSAGE_MAX = 2000;

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return waitingPoolErrorMessage(error.code, error.message);
  return error instanceof Error ? error.message : "请求未能完成";
}

export function conversationName(summary: ConversationSummary): string {
  if (summary.counterpart.state === "erased") return "已删除的账号";
  return summary.counterpart.name ?? "已建立的连接";
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function clock(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(value));
}

function dayLabel(value: string, now = new Date()): string {
  const date = new Date(value);
  if (sameDay(date, now)) return "今天";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return "昨天";
  return new Intl.DateTimeFormat("zh-CN", date.getFullYear() === now.getFullYear()
    ? { month: "long", day: "numeric" }
    : { year: "numeric", month: "long", day: "numeric" }).format(date);
}

function listTime(value: string, now = new Date()): string {
  const date = new Date(value);
  if (sameDay(date, now)) return clock(value);
  return new Intl.DateTimeFormat("zh-CN", date.getFullYear() === now.getFullYear()
    ? { month: "numeric", day: "numeric" }
    : { year: "numeric", month: "numeric", day: "numeric" }).format(date);
}

/** Why sending is closed, said once, in the place the composer would be. */
function closedNote(summary: ConversationSummary): string | null {
  const name = summary.counterpart.name;
  if (summary.sendState === "departed") {
    return summary.counterpart.state === "erased"
      ? "对方已删除账号，并抹去了 TA 说过的话。你自己的消息仍然保留。"
      : `${name ?? "对方"}已离开 Perhaps。对话会保留，但不能再发送消息。`;
  }
  if (summary.sendState === "you_blocked") return "你已屏蔽 TA，双方都不能再发送消息。可以在「关于 TA」里解除。";
  if (summary.sendState === "unavailable") return "现在不能给 TA 发送消息。";
  return null;
}

/** The list the Connections tab shows, kept fresh in the background so the tab can mark new messages. */
export function useConversations(enabled: boolean) {
  const [items, setItems] = useState<ConversationSummary[] | null>(null);
  const [error, setError] = useState("");
  const refresh = useCallback(async () => {
    try {
      const result = await api<{ conversations: ConversationSummary[] }>("/api/v1/pairing/conversations");
      setItems(result.conversations);
      setError("");
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }, []);
  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const tick = () => { if (document.visibilityState === "visible") void refresh(); };
    const timer = window.setInterval(tick, LIST_POLL_MS);
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", tick);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", tick); document.removeEventListener("visibilitychange", tick); };
  }, [enabled, refresh]);
  return { items, error, refresh, unread: Boolean(items?.some((item) => item.unreadCount > 0)) };
}

function Monogram({ summary }: { summary: ConversationSummary }) {
  const name = summary.counterpart.state === "erased" ? "" : summary.counterpart.name ?? "";
  return <span className="r4-monogram" aria-hidden="true">{Array.from(name)[0] ?? ""}</span>;
}

function preview(summary: ConversationSummary): string {
  const last = summary.lastMessage;
  if (!last) return "你们已经建立连接";
  if (last.text === null) return "这条消息已被抹去";
  return `${last.mine ? "你：" : ""}${last.text.replace(/\s+/gu, " ")}`;
}

export function ConversationList({ items, error, onOpen, onRetry, offer }: {
  items: ConversationSummary[] | null;
  error: string;
  onOpen: (summary: ConversationSummary) => void;
  onRetry: () => void;
  offer?: ReactNode;
}) {
  if (items === null) {
    return error
      ? <section className="pairing-empty"><h2>暂时无法读取</h2><p role="alert">{error}</p><button className="button" onClick={onRetry}>Try again</button></section>
      : <LoadingState />;
  }
  if (items.length === 0) {
    return <section className="r4-empty-scene"><div className="r4-empty-content"><EmptyMark /><h2>No connections yet</h2><p>双方都愿意进一步认识之后，可以在这里说话。</p></div></section>;
  }
  return <section className="r4-conversations" aria-labelledby="conversations-heading">
    <h2 className="visually-hidden" id="conversations-heading">Connections</h2>
    <div className="r4-group r4-thread-list">
      {items.map((summary) => {
        const name = conversationName(summary);
        const at = summary.lastMessageAt ?? summary.connectedAt ?? null;
        return <button key={summary.connectionId} className="r4-thread-row" data-state={summary.counterpart.state}
          aria-label={`${name}${summary.unreadCount ? `，${summary.unreadCount} 条新消息` : ""}`} onClick={() => onOpen(summary)}>
          <Monogram summary={summary} />
          <span className="r4-thread-text">
            <span className="r4-thread-top"><strong>{name}</strong>{at && <time dateTime={at}>{listTime(at)}</time>}</span>
            <span className="r4-thread-preview" data-erased={summary.lastMessage?.text === null || undefined}>
              {summary.counterpart.state === "departed" && <em>已离开 Perhaps · </em>}{preview(summary)}
            </span>
          </span>
          {summary.unreadCount > 0 && <span className="r4-unread" aria-hidden="true" />}
        </button>;
      })}
    </div>
    {error && <p className="error-message" role="alert">{error}</p>}
    {offer}
  </section>;
}

/** A placeholder with no content in it: the shape of something once said, never its words. */
function Mist({ seq }: { seq: number }) {
  const first = 46 + (seq * 37) % 42;
  const second = 28 + (seq * 53) % 44;
  return <span className="r4-bubble r4-mist" role="img" aria-label="已抹去的消息"><i style={{ width: `${first}%` }} /><i style={{ width: `${second}%` }} /></span>;
}

interface PendingMessage { key: string; text: string; state: "sending" | "failed" }

type Row =
  | { kind: "day"; key: string; label: string }
  | { kind: "message"; key: string; message: ConversationMessage; end: boolean }
  | { kind: "pending"; key: string; pending: PendingMessage };

function rows(messages: ConversationMessage[], pending: PendingMessage[]): Row[] {
  const result: Row[] = [];
  let lastDay = "";
  messages.forEach((message, index) => {
    const day = dayLabel(message.sentAt);
    if (day !== lastDay) { result.push({ kind: "day", key: `day-${message.id}`, label: day }); lastDay = day; }
    const next = messages[index + 1];
    const end = !next || next.mine !== message.mine || Date.parse(next.sentAt) - Date.parse(message.sentAt) > 5 * 60_000
      || dayLabel(next.sentAt) !== day;
    result.push({ kind: "message", key: message.id, message, end });
  });
  if (pending.length && lastDay !== "今天") result.push({ kind: "day", key: "day-pending", label: "今天" });
  pending.forEach((item) => result.push({ kind: "pending", key: item.key, pending: item }));
  return result;
}

export function ConversationThread({ initial, onBack, onAbout, onChanged }: {
  initial: ConversationSummary;
  onBack: () => void;
  onAbout: () => void;
  onChanged: () => void;
}) {
  const [summary, setSummary] = useState(initial);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [hasEarlier, setHasEarlier] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [starter, setStarter] = useState<string | null>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const lastSeq = useRef(0);
  const readSeq = useRef(0);
  const stick = useRef(true);
  const keepOffset = useRef<number | null>(null);
  const viewportStyle = useVisualViewport();
  const connectionId = initial.connectionId;
  const name = conversationName(summary);

  const scroller = () => anchor.current?.closest<HTMLElement>(".r4-body") ?? null;

  const merge = useCallback((incoming: ConversationMessage[]) => {
    if (!incoming.length) return;
    setMessages((current) => {
      const known = new Set(current.map((message) => message.id));
      const next = [...current, ...incoming.filter((message) => !known.has(message.id))].sort((a, b) => a.seq - b.seq);
      lastSeq.current = Math.max(lastSeq.current, next[next.length - 1]?.seq ?? 0);
      return next;
    });
  }, []);

  const markRead = useCallback(async (seq: number) => {
    if (seq <= readSeq.current || document.visibilityState !== "visible") return;
    readSeq.current = seq;
    try {
      await api(`/api/v1/pairing/conversations/${connectionId}/read`, { method: "POST", body: JSON.stringify({ throughSeq: seq }) });
      onChanged();
    } catch { readSeq.current = 0; }
  }, [connectionId, onChanged]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const page = await api<ConversationPage>(`/api/v1/pairing/conversations/${connectionId}/messages`);
        if (cancelled) return;
        setSummary(page.conversation);
        setHasEarlier(page.hasEarlier);
        merge(page.messages);
        lastSeq.current = Math.max(page.conversation.lastSeq, lastSeq.current);
        void markRead(page.conversation.lastSeq);
        if (page.messages.length === 0 && page.conversation.counterpart.state === "present") {
          const answers = await api<{ counterpart: BeforeWeMeetAnswer[] }>(`/api/v1/pairing/connections/${connectionId}/before-we-meet`).catch(() => null);
          if (!cancelled) setStarter(answers?.counterpart.find((answer) => answer.promptId === 5)?.answerText ?? null);
        }
      } catch (caught) {
        if (!cancelled) setError(errorMessage(caught));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [connectionId, merge, markRead]);

  // New messages while the conversation is open; paused whenever the page is hidden.
  useEffect(() => {
    if (loading) return;
    const tick = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const page = await api<ConversationPage>(`/api/v1/pairing/conversations/${connectionId}/messages?after=${lastSeq.current}`);
        setSummary(page.conversation);
        merge(page.messages);
        if (page.messages.some((message) => !message.mine)) void markRead(page.conversation.lastSeq);
      } catch { /* The next tick tries again; a failed poll is not worth interrupting the person. */ }
    };
    const timer = window.setInterval(() => void tick(), POLL_MS);
    const visible = () => { if (document.visibilityState === "visible") void tick(); };
    document.addEventListener("visibilitychange", visible);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", visible); };
  }, [loading, connectionId, merge, markRead]);

  // Follow the newest message while the person is at the bottom; keep their place when earlier messages load.
  useLayoutEffect(() => {
    const node = scroller();
    if (!node) return;
    if (keepOffset.current !== null) {
      node.scrollTop = node.scrollHeight - keepOffset.current;
      keepOffset.current = null;
    } else if (stick.current) {
      node.scrollTop = node.scrollHeight;
    }
  }, [messages, pending, loading]);
  useEffect(() => {
    const node = scroller();
    if (!node) return;
    const remember = () => { stick.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80; };
    node.addEventListener("scroll", remember, { passive: true });
    return () => node.removeEventListener("scroll", remember);
  }, [loading]);

  const loadEarlier = async () => {
    const first = messages[0]?.seq;
    if (!first) return;
    try {
      const page = await api<ConversationPage>(`/api/v1/pairing/conversations/${connectionId}/messages?before=${first}`);
      const node = scroller();
      keepOffset.current = node ? node.scrollHeight - node.scrollTop : null;
      setHasEarlier(page.hasEarlier);
      merge(page.messages);
    } catch (caught) { setError(errorMessage(caught)); }
  };

  const deliver = async (item: PendingMessage) => {
    setPending((current) => current.map((entry) => entry.key === item.key ? { ...entry, state: "sending" } : entry));
    try {
      const result = await api<{ message: ConversationMessage }>(`/api/v1/pairing/conversations/${connectionId}/messages`, {
        method: "POST", headers: { "Idempotency-Key": item.key }, body: JSON.stringify({ text: item.text }),
      });
      merge([result.message]);
      setPending((current) => current.filter((entry) => entry.key !== item.key));
      setError("");
      onChanged();
    } catch (caught) {
      setPending((current) => current.map((entry) => entry.key === item.key ? { ...entry, state: "failed" } : entry));
      setError(errorMessage(caught));
      if (caught instanceof ApiError && caught.code === "message_send_unavailable") {
        const page = await api<ConversationPage>(`/api/v1/pairing/conversations/${connectionId}/messages?after=${lastSeq.current}`).catch(() => null);
        if (page) setSummary(page.conversation);
      }
    }
  };

  const send = () => {
    const text = draft.replace(/\s+$/u, "");
    if (!text.trim() || summary.sendState !== "open") return;
    const item: PendingMessage = { key: createIdempotencyKey(), text, state: "sending" };
    stick.current = true;
    setPending((current) => [...current, item]);
    setDraft("");
    void deliver(item);
    field.current?.focus();
  };

  useLayoutEffect(() => {
    const node = field.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${Math.min(node.scrollHeight, 148)}px`;
  }, [draft]);

  const closed = closedNote(summary);
  const empty = !loading && messages.length === 0 && pending.length === 0;
  const dock = closed
    ? <p className="r4-thread-closed" role="status">{closed}</p>
    : <form className="r4-composer" onSubmit={(event) => { event.preventDefault(); send(); }}>
      <label className="visually-hidden" htmlFor="message-draft">给 {name} 的消息</label>
      <textarea id="message-draft" ref={field} rows={1} value={draft} maxLength={MESSAGE_MAX} placeholder="写下想说的话"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => { if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) { event.preventDefault(); send(); } }} />
      <button className="button primary" disabled={!draft.trim()}>Send</button>
    </form>;

  return <ParticipantFrame screenKey={`conversation-${connectionId}`} title={name} onBack={onBack}
    className="r4-conversation" style={viewportStyle} balanced={empty}
    actions={<button className="r4-icon" aria-label={`关于 ${name}`} onClick={onAbout}><AppIcon name="me" /></button>}
    dock={loading ? undefined : dock}>
    <div ref={anchor} className="r4-thread-anchor">
      {loading ? <LoadingState /> : empty ? <section className="r4-thread-empty">
        <EmptyMark />
        <h2>你们已经建立连接</h2>
        {!closed && <p>可以从这里说第一句话。</p>}
        {starter && <figure className="r4-starter"><figcaption>{BEFORE_WE_MEET_PROMPTS[4]}</figcaption><blockquote>{starter}</blockquote></figure>}
      </section> : <>
        {hasEarlier && <button className="text-button r4-earlier" onClick={() => void loadEarlier()}>Earlier messages</button>}
        <ol className="r4-thread" aria-label={`与 ${name} 的对话`}>
          {rows(messages, pending).map((row) => {
            if (row.kind === "day") return <li key={row.key} className="r4-day" aria-hidden="true">{row.label}</li>;
            if (row.kind === "pending") {
              return <li key={row.key} className="r4-message mine end" data-state={row.pending.state}>
                <p className="r4-bubble"><span className="visually-hidden">你：</span>{row.pending.text}</p>
                {row.pending.state === "failed"
                  ? <button className="text-button r4-message-retry" onClick={() => void deliver(row.pending)}>未能发送 · Try again</button>
                  : <span className="r4-message-time">发送中</span>}
              </li>;
            }
            const { message } = row;
            return <li key={row.key} className={`r4-message ${message.mine ? "mine" : "theirs"}${row.end ? " end" : ""}`}>
              {message.text === null ? <Mist seq={message.seq} />
                : <p className="r4-bubble"><span className="visually-hidden">{message.mine ? "你" : name}：</span>{message.text}</p>}
              {row.end && <time className="r4-message-time" dateTime={message.sentAt}>{clock(message.sentAt)}</time>}
            </li>;
          })}
        </ol>
      </>}
      {error && !loading && <p className="error-message r4-thread-error" role="alert">{error}</p>}
    </div>
  </ParticipantFrame>;
}

const REPORT_CATEGORIES = [
  ["harassment", "骚扰"],
  ["threat", "威胁或恐吓"],
  ["impersonation", "冒充他人"],
  ["scam", "诈骗或索要钱财"],
  ["inappropriate", "不当内容"],
  ["other", "其他"],
] as const;

type Pending = "block" | "unblock" | "report" | "hide";

export function ConversationAbout({ summary, onBack, onChanged, onRemoved }: {
  summary: ConversationSummary;
  onBack: () => void;
  onChanged: (next: ConversationSummary) => void;
  onRemoved: () => void;
}) {
  const [connection, setConnection] = useState<ConnectionEntry | null>(null);
  const [contacts, setContacts] = useState<ContactEntry[] | null>(null);
  const [answers, setAnswers] = useState<BeforeWeMeetAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogKind, setDialogKind] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [category, setCategory] = useState("");
  const [note, setNote] = useState("");
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [theirMessages, setTheirMessages] = useState<ConversationMessage[]>([]);
  const [attached, setAttached] = useState<string[]>([]);
  const reportKey = useRef(createIdempotencyKey());
  const dialog = useRef<HTMLDialogElement>(null);
  const present = summary.counterpart.state === "present";
  const name = conversationName(summary);
  const id = summary.connectionId;

  useEffect(() => {
    if (!present) { setLoading(false); return; }
    void (async () => {
      const [list, shared, before] = await Promise.all([
        api<{ connections: ConnectionEntry[] }>("/api/v1/pairing/connections").catch(() => null),
        api<{ contacts: ContactEntry[] }>(`/api/v1/pairing/connections/${id}/contacts`).catch(() => null),
        api<{ counterpart: BeforeWeMeetAnswer[] }>(`/api/v1/pairing/connections/${id}/before-we-meet`).catch(() => null),
      ]);
      setConnection(list?.connections.find((entry) => entry.connectionId === id) ?? null);
      setContacts(shared?.contacts ?? null);
      setAnswers(before?.counterpart ?? []);
      setLoading(false);
    })();
  }, [id, present]);

  const open = async (kind: Pending) => {
    setError(""); setNotice(""); setDialogKind(kind);
    if (kind === "report") {
      reportKey.current = createIdempotencyKey();
      setCategory(""); setNote(""); setAlsoBlock(false); setAttached([]);
      const page = await api<ConversationPage>(`/api/v1/pairing/conversations/${id}/messages`).catch(() => null);
      setTheirMessages((page?.messages ?? []).filter((message) => !message.mine && message.text !== null).slice(-20).reverse());
    }
    if (kind !== "block") window.setTimeout(() => dialog.current?.showModal(), 0);
  };
  const close = () => { if (busy) return; dialog.current?.close(); setDialogKind(null); };

  const confirm = async () => {
    if (!dialogKind) return;
    setBusy(true); setError("");
    try {
      if (dialogKind === "hide") {
        await api(`/api/v1/pairing/conversations/${id}/hide`, { method: "POST", body: "{}" });
        dialog.current?.close(); setDialogKind(null); onRemoved();
        return;
      }
      if (dialogKind === "report") {
        await api(`/api/v1/pairing/conversations/${id}/reports`, {
          method: "POST", headers: { "Idempotency-Key": reportKey.current },
          body: JSON.stringify({ category, description: note.trim() || null, messageIds: attached }),
        });
      }
      if (dialogKind === "unblock") {
        const result = await api<{ conversation: ConversationSummary }>(`/api/v1/pairing/conversations/${id}/unblock`, { method: "POST", body: "{}" });
        onChanged(result.conversation);
      }
      setNotice(dialogKind === "report"
        ? "举报已提交。Perhaps 不会告诉 TA 这次举报。"
        : dialogKind === "unblock" ? "已解除屏蔽。你们又可以在这里发送消息了。" : "已屏蔽。你们都不能再在这里发送消息。");
      dialog.current?.close(); setDialogKind(dialogKind === "report" && alsoBlock ? "block" : null);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const canConfirm = dialogKind !== "report" || category !== "";
  return <ParticipantFrame screenKey={`conversation-about-${id}`} title={name} onBack={onBack}>
    <main className="r4-about">
      {loading ? <LoadingState /> : <>
        {present && (connection?.profile
          ? <CandidateProfile envelope={connection.profile} />
          : <p className="quiet-notice">对方的资料暂时看不到。</p>)}
        {!present && <p className="r4-about-departed">{summary.counterpart.state === "erased"
          ? "对方已删除账号，并抹去了 TA 说过的话。"
          : `${name}已离开 Perhaps，并把对话留给了你。`}</p>}
        {present && <section className="connection-subsection"><h3>TA 分享的联系方式</h3>
          {contacts === null ? <p>现在看不到 TA 的联系方式。</p>
            : contacts.length === 0 ? <p>对方暂未分享联系方式</p>
              : <dl className="contact-list">{contacts.map((entry) => <div key={entry.entryId}><dt>{entry.customLabel ?? CHANNEL_LABELS[entry.channelType]}</dt><dd>{entry.value}</dd></div>)}</dl>}
        </section>}
        {present && answers.length > 0 && <section className="connection-subsection"><h3>TA 在见面前想说的话</h3>
          {answers.map((answer) => <div className="before-answer" key={answer.promptId}><h4>{BEFORE_WE_MEET_PROMPTS[answer.promptId - 1]}</h4><p>{answer.answerText}</p></div>)}
        </section>}
        {notice && <p className="success-message" role="status">{notice}</p>}
        <MenuGroup label="This conversation">
          {present && <MenuRow onClick={() => void open("report")}>举报</MenuRow>}
          {present && (summary.sendState === "you_blocked"
            ? <MenuRow onClick={() => void open("unblock")}>解除屏蔽</MenuRow>
            : <MenuRow onClick={() => void open("block")}>屏蔽 TA</MenuRow>)}
          <MenuRow onClick={() => void open("hide")}>删除这段对话</MenuRow>
        </MenuGroup>
      </>}
    </main>
    {dialogKind === "block" && <BlockReasonSheet onClose={() => setDialogKind(null)} onReport={() => void open("report")} onSave={async (input) => {
      const result = await api<{ conversation: ConversationSummary }>(`/api/v1/pairing/conversations/${id}/block`, { method: "POST", body: JSON.stringify(input) });
      onChanged(result.conversation);
      setNotice("已屏蔽。你们都不能再在这里发送消息。");
      // Drop already-rendered disclosure when blocking, just as a fresh About read does.
      setContacts(null); setAnswers([]); setConnection(null);
    }} />}
    <dialog ref={dialog} className="account-dialog r4-safety-dialog" onCancel={(event) => { if (busy) event.preventDefault(); else close(); }}>
      {dialogKind && dialogKind !== "block" && <>
        <h2>{dialogKind === "report" ? "举报 TA" : dialogKind === "unblock" ? "解除屏蔽？" : "删除这段对话？"}</h2>
        {dialogKind === "unblock" && <p className="dialog-copy">解除后，你们又可以在这里发送消息，TA 也能重新读取你分享的联系方式。Perhaps 不会通知 TA。</p>}
        {dialogKind === "hide" && <p className="dialog-copy">{summary.sendState === "departed"
          ? "对方已经离开，删除后这段对话将不再存在。"
          : "只删除你这一边的记录，对方的记录不受影响。连接仍然保留，之后的新消息还会出现在这里。"}</p>}
        {dialogKind === "report" && <>
          <p className="dialog-copy">只有你附上的消息和说明会交给 Perhaps 的运营方处理，处理结束后删除。Perhaps 不会告诉 TA 这次举报。</p>
          <fieldset className="r4-report-field"><legend>原因</legend>
            {REPORT_CATEGORIES.map(([value, label]) => <label key={value} className="r4-option"><input type="radio" name="report-category" value={value} checked={category === value} onChange={() => setCategory(value)} /><span>{label}</span></label>)}
          </fieldset>
          {theirMessages.length > 0 && <fieldset className="r4-report-field"><legend>附上 TA 的消息（可选）</legend>
            {theirMessages.map((message) => <label key={message.id} className="r4-option r4-attach"><input type="checkbox" checked={attached.includes(message.id)}
              onChange={(event) => setAttached((current) => event.target.checked ? [...current, message.id] : current.filter((value) => value !== message.id))} />
              <span><small>{dayLabel(message.sentAt)} {clock(message.sentAt)}</small>{message.text}</span></label>)}
          </fieldset>}
          <label className="dialog-field"><span>补充说明（可选）</span><textarea value={note} maxLength={1000} rows={3} onChange={(event) => setNote(event.target.value)} /></label>
          {summary.sendState !== "you_blocked" && <label className="r4-option"><input type="checkbox" checked={alsoBlock} onChange={(event) => setAlsoBlock(event.target.checked)} /><span>举报后继续屏蔽 TA</span></label>}
        </>}
        {error && <p className="error-message" role="alert">{error}</p>}
        <div className="dialog-actions">
          <button className="button secondary" disabled={busy} onClick={close}>Cancel</button>
          <button className="button primary" disabled={busy || !canConfirm} aria-busy={busy || undefined} onClick={() => void confirm()}>{busy ? "Processing" : "Confirm"}</button>
        </div>
      </>}
    </dialog>
  </ParticipantFrame>;
}
